import { DragEvent, useEffect, useRef, useState } from "react";
import ShowMaterialMediaByContentType from "./ShowMaterialMediaByContentType";
import { InlineMath } from "react-katex";
import {
  constructInputWithSpecialExpressionList,
  checkAnswerState,
} from "../../../utilization/CatchtivityUtilization";
import i18n from "../../../language/i18n";
import { IOrderingActivityMaterialProps } from "../../../properties/ActivityProperties";
import { orderingActivityMaterialContentStyles } from "./OrderingActivityMaterialContentStyles";
import { resolveMaterialMediaType } from "../../../utilization/StorageUtilization";

// Reliable up/down reorder — each row moves by swapping its slot with the
// neighbour via the existing onChange(answerMap, slotA, slotB) contract.
const OrderingActivityMaterialContent = ({
  uniqueValue,
  answerMap,
  materialMap,
  contentMap,
  checkCanAnswerQuestion,
  onChange,
  isPreview,
  showCorrectAnswer,
}: IOrderingActivityMaterialProps) => {
  const [displayAnswerMap, setDisplayAnswerMap] = useState<any>(answerMap);

  useEffect(() => {
    if (showCorrectAnswer) {
      const correctAnswerMap: any = {};
      Object.keys(answerMap).forEach((answerKey, index) => {
        correctAnswerMap[answerKey] = index;
      });
      setDisplayAnswerMap(correctAnswerMap);
    } else {
      setDisplayAnswerMap(answerMap);
    }
  }, [showCorrectAnswer, answerMap]);

  const slotKeys = Object.keys(displayAnswerMap);
  const canAnswer = checkCanAnswerQuestion();
  const interactive = canAnswer && !showCorrectAnswer && !isPreview;

  const move = (index: number, direction: number) => {
    if (!interactive) return;
    const target = index + direction;
    if (target < 0 || target >= slotKeys.length) return;
    onChange(answerMap, slotKeys[index], slotKeys[target]);
  };

  // ── drag & drop reorder (desktop; ▲▼ stays as the touch/keyboard path) ──
  // Dropping ONTO a row INSERTS the dragged row there: a walk of adjacent
  // swaps through the wrapper's swap contract (it mutates the same answerMap,
  // so the chain accumulates and the last changeAnswer carries the final order).
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const moveTo = (from: number, to: number) => {
    if (!interactive || from === to) return;
    const step = to > from ? 1 : -1;
    for (let i = from; i !== to; i += step) {
      onChange(answerMap, slotKeys[i], slotKeys[i + step]);
    }
  };
  const pressStartedInPlayer = useRef(false);

  /**
   * Where the press that may become a drag landed. Capture phase, so it is recorded even though the
   * player stops these events on their way up.
   */
  const handleRowPointerDown = (e: { target: EventTarget | null }) => {
    pressStartedInPlayer.current = !!(
      e.target as HTMLElement | null
    )?.closest?.(".qbx-root");
  };

  const handleDragStart = (index: number) => (e: DragEvent) => {
    if (!interactive) return;
    // A row carrying a recording contains the audio player, and the row is dragged with NATIVE
    // HTML5 drag - which a React bubble-phase stopPropagation cannot cancel. So pressing and
    // dragging the scrubber would reorder the row instead of scrubbing.
    //
    // The check reads where the PRESS landed, recorded below, rather than this event's target:
    // dragstart fires at the drag SOURCE, which is the nearest draggable ancestor. Nothing inside
    // the player is draggable, so the target here is always the row itself and asking it for a
    // .qbx-root ancestor can only ever answer no.
    if (pressStartedInPlayer.current) {
      e.preventDefault();
      return;
    }
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    // Firefox needs data for the drag to start
    e.dataTransfer.setData("text/plain", String(index));
  };
  const handleDragOver = (index: number) => (e: DragEvent) => {
    if (!interactive || dragIndex == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIndex !== index) setOverIndex(index);
  };
  const handleDrop = (index: number) => (e: DragEvent) => {
    if (!interactive || dragIndex == null) return;
    e.preventDefault();
    moveTo(dragIndex, index);
    setDragIndex(null);
    setOverIndex(null);
  };
  const handleDragEnd = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  // Renders the content (text or media) for a given slot.
  const renderContent = (materialKey: string, index: number) =>
    resolveMaterialMediaType(materialMap[displayAnswerMap[materialKey]], contentMap.type) === "TEXT" ? (
      <p style={{ margin: 0 }}>
        {constructInputWithSpecialExpressionList(
          materialMap[displayAnswerMap[materialKey]],
        ).map((inputPart, partIndex) => (
          <span
            key={partIndex}
            className={`${inputPart.isBold ? "mo2-bold" : ""} ${
              inputPart.isUnderline ? "mo2-underline" : ""
            }`}
          >
            {inputPart.isEquation ? (
              <span className="mo2-xl">
                <InlineMath math={inputPart.value} />
              </span>
            ) : (
              inputPart.value
            )}
          </span>
        ))}
      </p>
    ) : (
      <div className="mo2-media-wrap">
        <ShowMaterialMediaByContentType
          key={`${uniqueValue}-${index}`}
          contentType={resolveMaterialMediaType(materialMap[displayAnswerMap[materialKey]], contentMap.type)}
          src={materialMap[displayAnswerMap[materialKey]]}
          canFullScreen={true}
        />
      </div>
    );

  return (
    <>
      <div className="mo2-root">
        {slotKeys.map((materialKey, index) => {
          // V4 (AssignmentActivityScreenV4): graded rows recolor green/red and
          // carry a right-aligned ✓/✕ mark.
          const learnerAnswerState = !isPreview
            ? null
            : checkAnswerState(displayAnswerMap[materialKey] + "", index + "");
          const rowModifier =
            learnerAnswerState === "CORRECT"
              ? " mo2-row--correct"
              : learnerAnswerState === "INCORRECT"
                ? " mo2-row--wrong"
                : "";
          return (
            <div
              className={`mo2-row${rowModifier}${
                dragIndex === index ? " is-dragging" : ""
              }${
                overIndex === index && dragIndex != null && dragIndex !== index
                  ? " is-dropover"
                  : ""
              }`}
              key={index}
              draggable={interactive}
              onPointerDownCapture={handleRowPointerDown}
              onDragStart={handleDragStart(index)}
              onDragOver={handleDragOver(index)}
              onDrop={handleDrop(index)}
              onDragEnd={handleDragEnd}
            >
              {interactive ? (
                <span className="mo2-grip" aria-hidden="true">
                  <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
                    <circle cx="2.5" cy="2.5" r="1.6" />
                    <circle cx="7.5" cy="2.5" r="1.6" />
                    <circle cx="2.5" cy="8" r="1.6" />
                    <circle cx="7.5" cy="8" r="1.6" />
                    <circle cx="2.5" cy="13.5" r="1.6" />
                    <circle cx="7.5" cy="13.5" r="1.6" />
                  </svg>
                </span>
              ) : null}
              {/* number bead */}
              <span className="mo2-num">{index + 1}</span>

              {/* content + graded mark; a wrong row reveals its correct
                  position in the shared "Doğru cevap" chip */}
              <span className="mo2-txt">
                {renderContent(materialKey, index)}
                {learnerAnswerState === "INCORRECT" ? (
                  <span className="aas-corr mo2-corr">
                    <span className="aas-corr-tag">
                      {i18n.t("correct_label")}
                    </span>
                    <span className="aas-corr-val">
                      {i18n.t("position_ordinal", {
                        position: Number(displayAnswerMap[materialKey]) + 1,
                      })}
                    </span>
                  </span>
                ) : null}
              </span>
              {learnerAnswerState ? (
                <span
                  className={`mo2-mark ${
                    learnerAnswerState === "CORRECT"
                      ? "mo2-mark--ok"
                      : "mo2-mark--no"
                  }`}
                >
                  {learnerAnswerState === "CORRECT" ? "✓" : "✕"}
                </span>
              ) : null}

              {/* up / down buttons — hidden once graded */}
              {interactive ? (
                <div className="mo2-btns">
                  <button
                    type="button"
                    className="mo2-btn"
                    aria-label={i18n.t("move_up")}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    className="mo2-btn"
                    aria-label={i18n.t("move_down")}
                    disabled={index === slotKeys.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    ▼
                  </button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <style>{orderingActivityMaterialContentStyles}</style>
    </>
  );
};

export default OrderingActivityMaterialContent;
